import React, { FC, useState } from 'react';
import { SwitchTransition } from 'react-transition-group';
import styled from 'styled-components';
import { useTranslation } from '../../utils/useTranslation';
import { Dialog, DialogContent, DialogTitle } from '../feedback/Dialog';
import { IconAppQr } from '../icon/IconAppQr';
import { IconArrowRight } from '../icon/IconArrowRight';
import { IconDownloadFomStores } from '../icon/IconDownloadFromStores';
import { IconFillClose } from '../icon/IconFillClose';
import { IconLogo } from '../icon/IconLogo';
import { Button } from '../input/Button';
import { IconButton } from '../input/IconButton';
import { Box } from '../layout/Box';
import { Flex } from '../layout/Flex';
import { makeTransitionComponent } from '../transition/makeTransitionComponent';
import { Typography } from '../typography/Typography';

const TransitionSlideUp = makeTransitionComponent((timeout) => ({
    default: {
        opacity: 0,
        transform: 'translateY(100%) translateX(-50%)',
        transitionDelay: '0ms',
        transitionDuration: `${timeout}ms`,
        transitionTimingFunction: 'cubic-bezier(0, 0, 0.2, 1)',
        transitionProperty: 'transform, opacity',
    },
    entering: { transform: 'translateY(100%) translateX(-50%)', opacity: 0 },
    entered: { transform: 'translateY(0) translateX(-50%)', opacity: 1 },
}));

const Container = styled(Box).attrs({
    backgroundColor: 'var(--color-invert-blur)',
    backdropFilter: 'blur(25px)',
    position: 'fixed',
    bottom: '0.5em',
    left: '50%',
    width: '640px',
    borderRadius: '24px',
    padding: '16px 24px',
    overflow: 'hidden',
})``;

interface AppDiscoveryDescProps {
    onClose: () => void;
}

enum AppDiscoveryDescStep {
    SNACKBAR = 'snackbar',
    DIALOG = 'dialog',
}

export const AppDiscoveryDesc: FC<AppDiscoveryDescProps> = ({ onClose }) => {
    const [step, setStep] = useState<AppDiscoveryDescStep>(AppDiscoveryDescStep.SNACKBAR);
    const t = useTranslation();

    if (step === AppDiscoveryDescStep.SNACKBAR) {
        return (
            <SwitchTransition>
                <TransitionSlideUp>
                    <Container>
                        <Flex direction="row" alignItems="center" justifyContent="space-between">
                            <Flex direction="row" alignItems="center">
                                <Box ml={-1}>
                                    <IconButton onClick={onClose}>
                                        <IconFillClose fontSize={24} color="var(--color-primary)" />
                                    </IconButton>
                                </Box>
                                <Box ml={1.5}>
                                    <IconLogo fontSize={48} />
                                </Box>
                                <Box ml={2.5} width="60%">
                                    <Typography
                                        variant="title3"
                                        color="var(--color-primary)"
                                        weight="medium"
                                    >
                                        {t('В приложении удобнее')}
                                    </Typography>
                                    <Typography
                                        variant="body2"
                                        color="var(--color-primary)"
                                        weight="regular"
                                    >
                                        {t('Скачайте приложение, чтобы получить все возможности')}
                                    </Typography>
                                </Box>
                            </Flex>
                            <Button
                                ml={2.5}
                                mr={1}
                                size="l"
                                variant="filled"
                                onClick={() => setStep(AppDiscoveryDescStep.DIALOG)}
                            >
                                {t('СКАЧАТЬ')}
                                <Box ml={1}>
                                    <IconArrowRight fontSize={16} color="var(--color-invert)" />
                                </Box>
                            </Button>
                        </Flex>
                    </Container>
                </TransitionSlideUp>
            </SwitchTransition>
        );
    }

    if (step === AppDiscoveryDescStep.DIALOG) {
        return (
            <Dialog onClose={onClose}>
                <DialogContent>
                    <DialogTitle onClose={onClose} withBorderBottom={false} />
                    <IconAppQr
                        fontSize={350}
                        style={{ margin: 'auto' }}
                        color="var(--color-primary)"
                    />
                    <Typography
                        variant="body"
                        color="var(--color-primary)"
                        weight="bold"
                        m="auto"
                        width="80%"
                        textAlign="center"
                    >
                        {t('Отсканируйте QR-код, чтобы скачать мобильное приложение')}
                    </Typography>
                    <Box m="auto" pt={2}>
                        <IconDownloadFomStores height="40px" width="405px" fontSize="500px" />
                    </Box>
                </DialogContent>
            </Dialog>
        );
    }

    return null;
};
